import React, { useState, useEffect, useMemo } from 'react';
import { Layout, Menu, Avatar, Typography, Tag, Badge } from 'antd';
import {
    GlobalOutlined,
    ThunderboltOutlined,
    TeamOutlined,
    FileProtectOutlined,
    FolderOpenOutlined,
    QuestionCircleOutlined,
    BookOutlined,
    BarChartOutlined,
    BugOutlined,
    LogoutOutlined,
    CheckSquareOutlined,
    UsergroupAddOutlined,
    CalendarOutlined,
    ShopOutlined,
    PieChartOutlined,
    CustomerServiceOutlined,
    ExperimentOutlined,
    SafetyCertificateOutlined,
    ApiOutlined,
    ReadOutlined,
    ContainerOutlined,
    EditOutlined,
    AuditOutlined,
    ReconciliationOutlined,
    AppstoreOutlined,
    SettingOutlined,
    DatabaseOutlined,
} from '@ant-design/icons';
import { useApp } from '../context/AppContext';
import { View } from '../constants/roles';
import { getGroupedMenuItems, findGroupKeyForView, loadOpenGroups, saveOpenGroups, normaliseOpenGroups, nextOpenGroups } from '../utils/sidebarMenu';
import { useTrainingAvailability } from '../hooks/useTrainingAvailability';

const { Sider } = Layout;
const { Text } = Typography;

/** Map view ids to Ant Design icons */
const groupIconMap = {
    'group:overview': <AppstoreOutlined />,
    'group:people': <TeamOutlined />,
    'group:content': <DatabaseOutlined />,
    'group:firm': <SettingOutlined />,
    'group:libraries': <BookOutlined />,
    'group:support': <CustomerServiceOutlined />,
};

const iconMap = {
    [View.DASHBOARD]: <GlobalOutlined />,
    [View.LEAD_ADVISORS]: <UsergroupAddOutlined />,
    [View.ADMINISTRATORS]: <SafetyCertificateOutlined />,
    [View.ADVISORY_FIRM]: <ShopOutlined />,
    [View.INTEGRATIONS]: <ApiOutlined />,
    [View.PROJECT_WORKSPACE]: <ThunderboltOutlined />,
    [View.PROJECT_DASHBOARD]: <PieChartOutlined />,
    [View.PROJECT_MEMBERS]: <TeamOutlined />,
    [View.EVIDENCE]: <FileProtectOutlined />,
    [View.DOCUMENT_LIBRARY]: <FolderOpenOutlined />,
    [View.TASK_CENTER]: <CheckSquareOutlined />,
    [View.QUESTION_BANK]: <QuestionCircleOutlined />,
    [View.QUESTION_BANK_CURATOR]: <EditOutlined />,
    [View.POLICY_LIBRARY]: <BookOutlined />,
    [View.MASTER_DATA]: <BarChartOutlined />,
    [View.VULNERABILITY_MASTER]: <BugOutlined />,
    [View.MEETINGS]: <CalendarOutlined />,
    [View.SUPPORT]: <CustomerServiceOutlined />,
    [View.SQA_RESULTS]: <ExperimentOutlined />,
    [View.TRAINING]: <ReadOutlined />,
    [View.LIBRARY_TEMPLATES]: <ContainerOutlined />,
    [View.REGULATORY_DATABASE]: <AuditOutlined />,
    [View.REGULATORY_LIBRARY]: <AuditOutlined />,
    [View.PROJECT_REGULATIONS]: <ReconciliationOutlined />,
};

/**
 * Sidebar navigation – role-aware, project-aware, collapsible.
 * Now powered by Ant Design Layout.Sider + Menu.
 */
const Sidebar = () => {
    const {
        user,
        currentView,
        setCurrentView,
        sidebarOpen,
        toggleSidebar,
        activeProject,
        exitProject,
        taxonomyPendingCount,
    } = useApp();

    // Training is stage-gated at the API; only an explicit "closed" answer hides the entry.
    const { available: trainingAvailable } = useTrainingAvailability(activeProject?.id, !!user && user.role !== 'ADMIN');

    // ── Build nav items based on role + project context ──────
    // Item keys/labels/order come from utils/sidebarMenu.js (extracted for unit testing —
    // see .claude/lessons/frontend-role-comparison-casing-and-placeholder-id.md and the
    // Backlog #36/#37 comment preserved there); this wrapper only attaches the antd icon
    // JSX (and the taxonomy-pending Badge) that the pure module can't carry.
    const withIcon = (item) => {
        if (item.type === 'divider') return item;
        if (item.children) return { ...item, icon: groupIconMap[item.key], children: item.children.map(withIcon) };
        if (item.key === 'exit_project') {
            return { ...item, icon: <LogoutOutlined /> };
        }
        if (item.key === View.MASTER_DATA) {
            return {
                ...item,
                icon: (
                    <Badge dot={taxonomyPendingCount > 0} size="small" offset={[-4, 4]}>
                        <span className="policylens-sidebar-icon-wrap" aria-hidden>{iconMap[item.key]}</span>
                    </Badge>
                ),
            };
        }
        return { ...item, icon: iconMap[item.key] };
    };
    const groupedItems = useMemo(
        () => getGroupedMenuItems(user, activeProject, { trainingAvailable }),
        [user, activeProject, trainingAvailable],
    );
    const menuItems = groupedItems.map(withIcon);

    // Accordion: one top-level group open at a time, remembered per user. The group holding the
    // active view wins on load and on navigation; a stale multi-key value from older sessions is normalised.
    const activeGroup = findGroupKeyForView(groupedItems, currentView);
    const [openKeys, setOpenKeys] = useState(() => normaliseOpenGroups(loadOpenGroups(user), activeGroup));
    useEffect(() => {
        const next = normaliseOpenGroups(loadOpenGroups(user), activeGroup);
        setOpenKeys(next);
        saveOpenGroups(user, next);
    }, [user?.id, user?.email, user?.role]);
    useEffect(() => {
        if (!activeGroup) return;
        setOpenKeys([activeGroup]);
        saveOpenGroups(user, [activeGroup]);
    }, [activeGroup, currentView]);
    const handleOpenChange = (keys) => {
        const next = nextOpenGroups(openKeys, keys);
        setOpenKeys(next);
        saveOpenGroups(user, next);
    };

    const handleMenuClick = ({ key }) => {
        if (key === 'exit_project') {
            exitProject();
            return;
        }
        if (Object.values(View).includes(key)) {
            setCurrentView(key);
        }
    };

    return (
        <Sider
            collapsible
            collapsed={!sidebarOpen}
            onCollapse={toggleSidebar}
            width={260}
            collapsedWidth={72}
            theme="dark"
            style={{
                background: 'var(--color-sidebar-bg)',
                borderRight: '1px solid var(--color-sidebar-line)',
                overflow: 'hidden',
                height: '100vh',
                position: 'fixed',
                left: 0,
                top: 0,
                bottom: 0,
                zIndex: 50,
            }}
            trigger={null}
        >
          <div
            data-testid="sidebar-column"
            style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
          >
            {/* Logo */}
            <div style={{
                flexShrink: 0,
                padding: sidebarOpen ? '20px 20px 16px' : '20px 0 16px',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                justifyContent: sidebarOpen ? 'flex-start' : 'center',
            }}>
                {/* Logo used exactly as supplied (brand identity: no recolouring, no extra marks). */}
                <img
                    src={`${process.env.PUBLIC_URL || ''}/brand/prolense-logo.png`}
                    alt="Pro Lence"
                    style={{ height: sidebarOpen ? 38 : 26, width: 'auto', display: 'block', maxWidth: '100%', objectFit: 'contain' }}
                />
            </div>

            {/* Active project context */}
            {activeProject && sidebarOpen && (
                <div style={{
                    flexShrink: 0,
                    margin: '0 16px 12px',
                    padding: '10px 14px',
                    background: 'rgba(255, 255, 255, 0.06)',
                    borderRadius: 10,
                    border: '1px solid var(--color-sidebar-line)',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <Text style={{ fontSize: 10, fontWeight: 700, color: '#64748b', letterSpacing: '0.1em' }}>
                            ACTIVE CONTEXT
                        </Text>
                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e' }} />
                    </div>
                    <Text style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0' }}>
                        {activeProject.name}
                    </Text>
                </div>
            )}

            {/* Navigation: the only scroller */}
            <div
                data-testid="sidebar-scroll"
                className="policylens-sidebar-scroll"
                style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}
            >
            <Menu
                theme="dark"
                mode="inline"
                selectedKeys={[currentView]}
                onClick={handleMenuClick}
                items={menuItems}
                openKeys={sidebarOpen ? openKeys : undefined}
                onOpenChange={handleOpenChange}
                style={{
                    background: 'transparent',
                    border: 'none',
                    padding: '0 8px',
                }}
            />
            </div>

            {/* User info pinned at bottom */}
            <div data-testid="sidebar-footer" style={{
                flexShrink: 0,
                background: 'var(--color-sidebar-bg)',
                padding: sidebarOpen ? '16px 20px' : '16px 0',
                borderTop: '1px solid var(--color-sidebar-line)',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                justifyContent: sidebarOpen ? 'flex-start' : 'center',
            }}>
                <Avatar
                    style={{
                        backgroundColor: 'var(--color-accent-fill)',
                        fontWeight: 700,
                        fontSize: 14,
                        flexShrink: 0,
                    }}
                    size={34}
                >
                    {user?.name?.charAt(0) || '?'}
                </Avatar>
                {sidebarOpen && (
                    <div style={{ minWidth: 0 }}>
                        <div style={{
                            color: '#e2e8f0',
                            fontSize: 13,
                            fontWeight: 700,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}>
                            {user?.name}
                        </div>
                        <Tag
                            color="geekblue"
                            style={{ fontSize: 10, fontWeight: 700, marginTop: 2, padding: '0 6px' }}
                        >
                            {user?.role?.replace(/_/g, ' ')}
                        </Tag>
                    </div>
                )}
            </div>
          </div>
        </Sider>
    );
};

export default Sidebar;
